import { getMakeupHairApiBaseUrl } from '../../makeuphair/utils/apiConfig';
import { kioskAuthHeaders } from '../../makeuphair/utils/kioskAuthHeaders';
import { appendFranchiseScopeQueryToPath } from '../../shared/franchiseFlowAuth';
import { imageRefToDataUrl } from '../../shared/imageRefToDataUrl';
import thumbManifest from '../data/hairStyleThumbManifest.json';
import { localHairStyleThumbPath } from '../data/promptStyleCatalog';

const PUBLIC_ROOT = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const LOCAL_THUMB_IDS = new Set(thumbManifest.style_ids || []);

function localPublicPath(path) {
  const segment = String(path || '').trim();
  if (!segment) return '';
  const normalized = segment.startsWith('/') ? segment : `/${segment}`;
  return `${PUBLIC_ROOT}${normalized}`;
}

function hairBase() {
  return `${getMakeupHairApiBaseUrl().replace(/\/$/, '')}/api/hair-styling`;
}

function withOrg(path) {
  return appendFranchiseScopeQueryToPath(path);
}

async function hairFetch(path, options = {}) {
  const res = await fetch(`${hairBase()}${withOrg(path)}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...kioskAuthHeaders({ 'Content-Type': 'application/json' }),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    const err = new Error(data?.error || `Request failed (${res.status})`);
    err.code = data?.code;
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchHairHealth() {
  return hairFetch('/health');
}

export async function fetchHairStyles(category, gender) {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (gender) params.set('gender', gender);
  const qs = params.toString();
  return hairFetch(qs ? `/styles?${qs}` : '/styles');
}

export async function fetchHairColors() {
  return hairFetch('/colors');
}

export async function detectHairPresentation(body = {}) {
  return hairFetch('/presentation', { method: 'POST', body: JSON.stringify(body) });
}

export async function recommendHairLooks(body = {}) {
  return hairFetch('/recommend', { method: 'POST', body: JSON.stringify(body) });
}

export async function normalizeHairPrompt(prompt) {
  return hairFetch('/normalize-prompt', {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });
}

export async function segmentHairImage(image) {
  return hairFetch('/segment', { method: 'POST', body: JSON.stringify({ image }) });
}

export async function generateHairLook(body) {
  return hairFetch('/generate', { method: 'POST', body: JSON.stringify(body) });
}

/** Prompt-only FLUX (no masks / job queue) — same API as Hair FLUX lab. */
export async function generateHairPromptLook({
  image,
  prompt,
  wrapIdentity = true,
  styleId,
  hairLength,
  guestCurrentLength,
  hairProfile,
  genderHint,
  keepOriginalColor = true,
  promptHairColor,
}) {
  const base = `${getMakeupHairApiBaseUrl().replace(/\/$/, '')}/api/hair-flux-prompt`;
  const res = await fetch(`${base}${withOrg('/generate')}`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      image,
      prompt,
      wrap_identity: wrapIdentity,
      style_id: styleId || undefined,
      hair_length: hairLength || undefined,
      guest_current_length: guestCurrentLength || undefined,
      hair_profile: hairProfile || undefined,
      gender_hint: genderHint || undefined,
      keep_original_color: keepOriginalColor,
      prompt_hair_color:
        promptHairColor && promptHairColor.id && promptHairColor.id !== 'natural'
          ? {
              id: promptHairColor.id,
              name: promptHairColor.name,
              hex: promptHairColor.hex,
              prompt: promptHairColor.prompt,
            }
          : undefined,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    const err = new Error(data?.error || data?.code || `Request failed (${res.status})`);
    err.code = data?.code;
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function fetchHairJobStatus(jobId) {
  return hairFetch(`/status/${encodeURIComponent(jobId)}`);
}

/** Authenticated binary result (Redis TTL or legacy DB fallback). */
export async function fetchHairResultObjectUrl(jobId) {
  const path = withOrg(`/result/${encodeURIComponent(jobId)}`);
  const res = await fetch(`${hairBase()}${path}`, {
    credentials: 'include',
    headers: kioskAuthHeaders(),
  });
  if (!res.ok) {
    const err = new Error(`Result fetch failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function resolveHairResultUrl(relativeOrAbsolute) {
  const raw = String(relativeOrAbsolute || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  const base = getMakeupHairApiBaseUrl().replace(/\/$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${base}${withOrg(path)}`;
}

/**
 * Create a short-lived download URL for QR (phone scan).
 * @param {string} imageDataUrl
 * @param {string} [name]
 */
export async function createHairDownloadUrl(imageRef, name = 'hair-style') {
  const imageDataUrl = await imageRefToDataUrl(imageRef);
  const data = await hairFetch('/download-qr', {
    method: 'POST',
    body: JSON.stringify({ image: imageDataUrl, name }),
  });
  const token = typeof data.token === 'string' ? data.token.trim() : '';
  if (!token) throw new Error('Download link missing');
  return `${hairBase()}/download/${encodeURIComponent(token)}`;
}

/** True when this style id has a JPEG under public/hair-style-thumbs/. */
export function hasLocalHairStyleThumb(styleId) {
  return LOCAL_THUMB_IDS.has(String(styleId || '').trim());
}

/** Gender placeholder when a style JPEG is missing (never use men's ref for women_*). */
export function promptStyleThumbFallbackUrl(styleId) {
  const id = String(styleId || '').trim().toLowerCase();
  if (id.startsWith('women_')) {
    return localPublicPath('/hair-style-refs/female.png');
  }
  if (id.startsWith('men_') || id.startsWith('m_')) {
    return localPublicPath('/hair-style-refs/male.png');
  }
  return '';
}

/** Thumbnail for prompt-catalog style id — bundled public asset, not E2E API. */
export function promptStyleThumbUrl(styleId) {
  return localPublicPath(localHairStyleThumbPath(styleId));
}

/** Resolve hair studio thumb paths (local public assets; legacy API paths mapped to local). */
export function hairStyleThumbUrl(pathOrUrl) {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  if (
    raw.startsWith('/hair-style-thumbs/')
    || raw.startsWith('/hair-style-refs/')
    || raw.startsWith('/hub/')
    || raw.startsWith('/loader/')
  ) {
    return localPublicPath(raw);
  }
  const apiThumb = raw.match(/^\/api\/hair-styling\/thumbs\/([a-z0-9_]+)(\?.*)?$/i);
  if (apiThumb) {
    const sid = apiThumb[1].toLowerCase();
    if (sid === '_female' || sid === 'female' || sid === 'women') {
      return localPublicPath('/hair-style-refs/female.png');
    }
    if (sid === '_male' || sid === 'male' || sid === 'men') {
      return localPublicPath('/hair-style-refs/male.png');
    }
    return localPublicPath(`/hair-style-thumbs/${apiThumb[1]}.jpg`);
  }
  const base = getMakeupHairApiBaseUrl().replace(/\/$/, '');
  if (raw.startsWith('/api/hair-styling/')) {
    return `${base}${raw}`;
  }
  if (raw.startsWith('/')) {
    return localPublicPath(raw);
  }
  return raw;
}
